import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ChatMessage from './ChatMessage';
import OrderSummary from './OrderSummary';
import emailjs from '@emailjs/browser';
import { chatWithLLM } from '../../utils/llmHelper';
import { v4 as uuidv4 } from 'uuid';

const initialState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  orderItems: [],
  previousOrder: null,
  deliveryMethod: '',
  shippingAddress: null
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [orderData, setOrderData] = useState(initialState);
  const [isTyping, setIsTyping] = useState(false);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const messagesEndRef = useRef(null);
  const initializedRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0 && !initializedRef.current) {
      initializedRef.current = true;
      setMessages([{
        id: uuidv4(),
        content: "Hey, my name is Bolt and I'm here to help you place your apparel order inquiry. What's your name?",
        isUser: false,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  const addMessage = (content, isUser = false) => {
    setMessages(prev => [...prev, {
      id: uuidv4(),
      content,
      isUser,
      timestamp: new Date()
    }]);
  };

  const updateOrderData = (newData) => {
    setOrderData(prev => {
      const updated = { ...prev, ...newData };
      
      // Special handling for order items
      if (newData.orderItems?.length) {
        updated.orderItems = [...newData.orderItems];
      }

      return updated;
    });
  };

  const addBotMessage = async (content) => {
    setIsTyping(true);
    try {
      addMessage(content, false);
      const response = await chatWithLLM([...messages, { content, isUser: false }], orderData);
      if (response) {
        addMessage(response, false);
      }
    } catch (error) {
      console.error('Error getting bot response:', error);
      addMessage("I'm having trouble right now. Please try again or email us directly.", false);
    }
    setIsTyping(false);
  };

  const validateOrderData = () => {
    const { name, email, phone, deliveryMethod } = orderData;
    
    // Check required fields
    if (!name || !email || !phone || !deliveryMethod) {
      return false;
    }

    // Parse order items from messages
    const lastOrderMessage = messages.slice().reverse().find(msg => 
      msg.content.toLowerCase().includes('hoodies') || 
      msg.content.toLowerCase().includes('shirts') || 
      msg.content.toLowerCase().includes('hats')
    );

    if (lastOrderMessage) {
      const orderText = lastOrderMessage.content.toLowerCase();
      const items = [];
      
      // Extract quantities and types
      const matches = orderText.match(/(\d+)\s+((?:large|medium|small|xs|xl|xxl|xxxl|[xls])\s+)?(\w+)\s+(hoodies?|shirts?|hats?|tanks?)/gi);
      
      if (matches) {
        matches.forEach(match => {
          const [quantity, size, color, type] = match.split(/\s+/);
          items.push({
            quantity: parseInt(quantity),
            size: size || 'M',
            color: color || 'black',
            type: type.toLowerCase().includes('hoodie') ? 'Hoodies' :
                  type.toLowerCase().includes('shirt') ? 'T-Shirts' :
                  type.toLowerCase().includes('hat') ? 'Hats' : 'Custom Apparel'
          });
        });
        
        updateOrderData({ orderItems: items });
        return items.length > 0;
      }
    }

    return false;
  };

  const handleUserInput = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const input = userInput.trim();
    addMessage(input, true);
    setUserInput('');
    
    setIsTyping(true);
    try {
      // Extract name from first response
      if (messages.length === 1) {
        updateOrderData({ name: input });
      }

      // Extract email and phone
      const emailMatch = input.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
      const phoneMatch = input.match(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/);
      
      if (emailMatch) {
        updateOrderData({ email: emailMatch[0] });
      }
      if (phoneMatch) {
        updateOrderData({ phone: phoneMatch[0].replace(/[-.]/, '') });
      }

      // Extract delivery preference
      if (input.toLowerCase().includes('pickup')) {
        updateOrderData({ deliveryMethod: 'pickup' });
      } else if (input.toLowerCase().includes('shipping')) {
        updateOrderData({ deliveryMethod: 'shipping' });
      }

      const response = await chatWithLLM([...messages, { content: input, isUser: true }], orderData);
      addMessage(response, false);

      // Check if we have all required info after bot response
      if (validateOrderData()) {
        await handleOrderSubmission();
      }
    } catch (error) {
      console.error('Error processing message:', error);
      addMessage("I'm having trouble processing your request. Please try again or contact us directly.", false);
    }
    setIsTyping(false);
  };

  const handleOrderSubmission = async () => {
    try {
      console.log('Submitting order:', orderData);

      // Send admin notification
      await emailjs.send(
        'service_l3m3m0p',
        'template_contact_form',
        {
          from_name: orderData.name,
          from_email: orderData.email,
          company: orderData.company || 'N/A',
          project_type: 'Custom Apparel Order',
          message: `Order Details:\n${orderData.orderItems.map(item => 
            `${item.quantity}x ${item.size} ${item.color} ${item.type}`
          ).join('\n')}\n\nDelivery: ${orderData.deliveryMethod}`,
          to_email: 'highlinehoodies@gmail.com'
        },
        'B-OLwAMJiQ94ehf7y'
      );

      // Send customer confirmation
      await emailjs.send(
        'service_l3m3m0p',
        'template_customer_confir',
        {
          from_name: orderData.name,
          from_email: orderData.email,
          project_type: 'Custom Apparel Order',
          message: `Your order has been received! We'll review the details and get back to you shortly with pricing and next steps.`,
          year: new Date().getFullYear()
        },
        'B-OLwAMJiQ94ehf7y'
      );
      
      console.log('Emails sent successfully');
      addMessage("Great! I've submitted your order and sent confirmation emails to both you and our team. You should receive them shortly. Is there anything else I can help you with?", false);
      setOrderData(initialState);
    } catch (error) {
      console.error('Order submission error:', error);
      addMessage("I'm sorry, there was an error submitting your order. Please try again or contact us directly at highlinehoodies@gmail.com", false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-cyan-500 text-black rounded-full p-4 shadow-lg hover:bg-cyan-400 transition-colors"
        >
          Chat with us
        </button>
      ) : (
        <div className="bg-gray-900 rounded-lg shadow-xl w-96 max-w-full border border-cyan-500/20">
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            <h3 className="text-lg font-semibold text-cyan-500">Highline Assistant</h3>
            <button
              onClick={() => {
                setIsOpen(false);
                initializedRef.current = false;
                setMessages([]);
              }}
              className="text-gray-400 hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="h-96 overflow-y-auto p-4 space-y-4">
            {messages.map(message => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isTyping && (
              <div className="flex items-center space-x-2 text-gray-400">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleUserInput} className="p-4 border-t border-gray-800">
            <div className="flex space-x-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <button
                type="submit"
                className="bg-cyan-500 text-black px-4 py-2 rounded-lg hover:bg-cyan-400 transition-colors"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatBot;